import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { List, X, LockKey } from "@phosphor-icons/react";
import { BUSINESS } from "@/config";

const links = [
  { label: "Search", href: "#search" },
  { label: "Promotions", href: "#promotions" },
  { label: "Wholesale", href: "#wholesale" },
  { label: "Reviews", href: "#reviews" },
  { label: "Visit Us", href: "#location" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-2.5 h-2.5 bg-primary rounded-full group-hover:scale-125 transition-transform" />
          <span className="font-display font-black text-lg sm:text-xl tracking-tighter uppercase">{BUSINESS.name}</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors tracking-wide"
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/login"
            className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors tracking-wide"
            data-testid="nav-admin"
          >
            <LockKey size={16} weight="bold" /> Admin
          </Link>
          <button
            onClick={() => go("#wholesale")}
            className="bg-primary text-white font-bold text-sm px-5 py-2.5 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide"
            data-testid="nav-apply-cta"
          >
            Apply Wholesale
          </button>
        </nav>

        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={28} /> : <List size={28} />}
        </button>
      </div>

      {open && (
        <motion.nav
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-5 py-4 flex flex-col gap-1"
        >
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-left py-3 text-base font-semibold text-foreground/90 hover:text-primary border-b border-white/5"
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/login"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 py-3 text-base font-semibold text-foreground/90 hover:text-primary border-b border-white/5"
            data-testid="nav-mobile-admin"
          >
            <LockKey size={18} weight="bold" /> Admin login
          </Link>
          <button
            onClick={() => go("#wholesale")}
            className="mt-3 bg-primary text-white font-bold py-3 rounded-sm uppercase tracking-wide"
          >
            Apply Wholesale
          </button>
        </motion.nav>
      )}
    </motion.header>
  );
};
